import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import {
  AlertCircle,
  Archive,
  Check,
  CheckCircle2,
  ChevronDown,
  FileText,
  MoreVertical,
  Pencil,
  Send,
  Voicemail,
  PhoneCall,
  Plus,
  User,
  Clock,
  UserPlus,
  X,
} from "lucide-react";
import sh from "bundles/common/utils/sh";
import { useSelector } from "react-redux";
import { formatRegionMessage } from "bundles/common/utils/utils";
import ApplyToOfferDialog from "bundles/Internal/components/common/ApplyToOfferDialog";
import TimePicker from "./TimePicker";
import DateTimeSelect from "./DateTimeSelect";

function defaultSignUpLaterValue() {
  const pad = (n) => String(n).padStart(2, "0");
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultFollowUpLaterValue() {
  const pad = (n) => String(n).padStart(2, "0");
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
import OutcomeSelect from "./OutcomeSelect";

export const outcomeOptions = [
  { value: "not_contacted", label: "Not contacted" },
  { value: "to_be_checked", label: "To be checked" },
  { value: "no_answer", label: "No answer" },
  { value: "voicemail", label: "Voicemail" },
  { value: "not_interested", label: "Not interested" },
  { value: "follow_up_today", label: "To be called today" },
  { value: "follow_up_later", label: "Follow up later" },
  { value: "sign_up_later", label: "Sign up later" },
  { value: "signed_up", label: "Signed up" },
  { value: "applied", label: "Applied" },
  { value: "email_sent", label: "Email sent" },
];

export const avatarColors = [
  { bg: "rgb(254 226 226 / 1)", text: "rgb(185 28 28 / 1)" },
  { bg: "rgb(219 234 254 / 1)", text: "rgb(29 78 216 / 1)" },
  { bg: "rgb(254 243 199 / 1)", text: "rgb(180 83 9 / 1)" },
  { bg: "rgb(204 251 241 / 1)", text: "rgb(15 118 110 / 1)" },
  { bg: "rgb(243 232 255 / 1)", text: "rgb(126 34 206 / 1)" },
];

export function getAvatarColors(id) {
  const idx = String(id || "0").split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % avatarColors.length;
  return avatarColors[idx];
}

export function outcomeLabel(value) {
  return outcomeOptions.find((option) => option.value === value)?.label || value;
}

export function outcomeVisual(outcome) {
  const normalized = String(outcome || "");
  if (["signed_up", "applied"].includes(normalized)) {
    return { icon: <Check size={12} />, bg: "hsl(142 71% 95%)", text: "hsl(142 50% 25%)" };
  }
  if (["sign_up_later"].includes(normalized)) {
    return { icon: <AlertCircle size={12} />, bg: "hsl(262 60% 95%)", text: "hsl(262 50% 35%)" };
  }
  if (["not_interested"].includes(normalized)) {
    return { icon: <X size={12} />, bg: "hsl(0 72% 95%)", text: "hsl(0 72% 35%)" };
  }
  if (["no_answer"].includes(normalized)) {
    return { icon: <X size={12} />, bg: "hsl(140 8% 90%)", text: "hsl(150 10% 46%)" };
  }
  if (["follow_up_today", "follow_up_later", "to_be_checked"].includes(normalized)) {
    return { icon: <AlertCircle size={12} />, bg: "hsl(38 92% 95%)", text: "hsl(38 70% 30%)" };
  }
  if (["voicemail"].includes(normalized)) {
    return { icon: <Voicemail size={12} />, bg: "hsl(140 8% 90%)", text: "hsl(150 10% 46%)" };
  }
  if (["email_sent"].includes(normalized)) {
    return { icon: <Send size={12} />, bg: "hsl(217 91% 95%)", text: "hsl(217 70% 30%)" };
  }
  return { icon: <PhoneCall size={12} />, bg: "hsl(140 8% 90%)", text: "hsl(150 10% 46%)" };
}

export function getOutcomeBadge(outcome) {
  if (!outcome) return { label: "Not contacted yet", bg: "hsl(217 91% 95%)", text: "hsl(217 70% 30%)" };
  const normalized = String(outcome).toLowerCase().replace(/\s+/g, "_");
  const map = {
    not_contacted: { label: "Not contacted", bg: "hsl(217 91% 95%)", text: "hsl(217 70% 30%)" },
    voicemail: { label: "Voicemail", bg: "hsl(140 8% 90%)", text: "hsl(150 30% 12%)" },
    not_interested: { label: "Not interested", bg: "hsl(0 72% 95%)", text: "hsl(0 72% 35%)" },
    no_answer: { label: "No answer", bg: "hsl(140 8% 90%)", text: "hsl(150 10% 46%)" },
    follow_up_today: { label: "To be called today", bg: "hsl(38 92% 95%)", text: "hsl(38 70% 30%)" },
    follow_up_later: { label: "Follow up later", bg: "hsl(38 92% 95%)", text: "hsl(38 70% 30%)" },
    to_be_checked: { label: "To be checked", bg: "hsl(38 92% 95%)", text: "hsl(38 70% 30%)" },
    sign_up_later: { label: "Sign up later", bg: "hsl(262 60% 95%)", text: "hsl(262 50% 35%)" },
    signed_up: { label: "Signed up", bg: "hsl(142 71% 95%)", text: "hsl(142 50% 25%)" },
    applied: { label: "Applied", bg: "hsl(217 91% 95%)", text: "hsl(217 70% 30%)" },
    email_sent: { label: "Email sent", bg: "hsl(217 91% 95%)", text: "hsl(217 70% 30%)" },
  };
  return map[normalized] || { label: outcome, bg: "hsl(140 8% 90%)", text: "hsl(150 10% 46%)" };
}

function formatIsoDate(isoValue) {
  if (!isoValue) return "No date";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleString();
}

function formatNoteDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CallHistoryItem = ({ call }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const noteRef = useRef(null);
  const visual = outcomeVisual(call.outcome);
  const hasNotes = Boolean(call.notes);
  const displayName = call.recruiter_name || call.recruiter_email || "Unknown";

  useEffect(() => {
    const el = noteRef.current;
    if (!el) return;
    setIsClamped(el.scrollHeight > el.clientHeight);
  }, [call.notes]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: visual.bg, color: visual.text, mt: "2px", flexShrink: 0 }}>
          {visual.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{outcomeLabel(call.outcome)}</Typography>
          <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)" }}>{displayName}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", flexShrink: 0, whiteSpace: "nowrap" }}>{formatIsoDate(call.created_at)}</Typography>
      </Box>
      {hasNotes && (
        <Box sx={{ mt: 0.5, pl: "34px" }}>
          <Typography
            ref={noteRef}
            sx={{
              fontSize: 13,
              color: "hsl(150 30% 12%)",
              lineHeight: 1.5,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              whiteSpace: "pre-wrap",
              ...(!expanded && {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
            }}
          >
            {call.notes}
          </Typography>
          {isClamped && (
            <Box
              component="button"
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 0,
                mt: 0.25,
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                fontSize: 12,
                color: "hsl(150 10% 46%)",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                "&:hover": { color: "hsl(150 30% 12%)" },
              }}
            >
              <ChevronDown
                size={13}
                style={{
                  transition: "transform 0.15s ease",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
              {expanded ? "Show less" : "Show more"}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const ProfileDetailPanel = ({ profile, onLogOutcome, onSignUpToCandidate, onArchive, onEdit, initialOfferIds = [], onApplied, hideApplyButton }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [pendingOutcome, setPendingOutcome] = useState(null);
  const [callbackTime, setCallbackTime] = useState("");
  const [callbackDateTime, setCallbackDateTime] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [applyOpen, setApplyOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const colors = getAvatarColors(profile?.id);
  const textareaRef = useRef(null);

  useEffect(() => {
    setPendingOutcome(null);
    setCallbackTime("");
    setCallbackDateTime("");
    setCallNotes("");
  }, [profile?.id]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    setLoadingNotes(true);
    sh.get(`/internal/external_profiles/${profile.id}/notes`)
      .then((res) => setNotes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setNotes([]))
      .finally(() => setLoadingNotes(false));
  }, [profile?.id]);

  const addNote = useCallback(() => {
    const content = noteContent.trim();
    if (!content || !profile?.id) return;

    setSavingNote(true);
    sh.post(`/internal/external_profiles/${profile.id}/notes`, { note: { content }, authenticity_token })
      .then((res) => {
        setNotes((prev) => [res.data, ...prev]);
        setNoteContent("");
      })
      .finally(() => setSavingNote(false));
  }, [profile?.id, noteContent, authenticity_token]);

  if (!profile) return null;

  return (
    <Stack spacing={2}>
      {/* Profile card */}
      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid hsl(140 8% 88%)", p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 40, height: 40, fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", bgcolor: colors.bg, color: colors.text }}>
              {(profile.first_name || "")[0]}{(profile.last_name || "")[0]}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                {[profile.first_name, profile.last_name].filter(Boolean).join(" ")}
              </Typography>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                {profile.desired_title || "No desired role"}
              </Typography>
            </Box>
          </Stack>
          {(onEdit || onArchive) && (
            <>
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  color: "hsl(150 10% 46%)",
                  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
                }}
              >
                <MoreVertical size={16} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    borderRadius: "10px",
                    border: "1px solid hsl(140 8% 88%)",
                    minWidth: 160,
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                {onEdit && (
                  <MenuItem
                    onClick={() => { setMenuAnchor(null); onEdit(profile); }}
                    sx={{
                      fontSize: 14,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(150 30% 12%)",
                      gap: 1.5,
                      px: 1.5,
                      py: 0.5,
                      "&:hover": { backgroundColor: "hsl(140 10% 95%)" },
                    }}
                  >
                    <Pencil size={15} />
                    Edit
                  </MenuItem>
                )}
                {onArchive && (
                  <MenuItem
                    onClick={() => { setMenuAnchor(null); onArchive(profile.id); }}
                    sx={{
                      fontSize: 14,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(0 60% 45%)",
                      gap: 1.5,
                      py: 0.5,
                      px: 1.5,
                      "&:hover": { backgroundColor: "hsl(0 60% 97%)" },
                    }}
                  >
                    <Archive size={15} />
                    Archive
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Stack>

        <Box sx={{ mt: 2.5, display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 1.5 }}>
          {[
            { label: "Phone", value: profile.phone || "-", copyValue: profile.phone },
            { label: "Email", value: profile.email || "-", copyValue: profile.email },
            { label: "Location", value: formatRegionMessage(profile.region) || "-" },
            { label: "Desired role", value: profile.desired_title || "-" },
          ].map((row) => (
            <Box key={row.label}>
              <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 0.3 }}>{row.label}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
                <Tooltip title={row.copyValue || ""} placement="top" arrow disableHoverListener={!row.copyValue}>
                  <Typography sx={{ fontSize: 14, color: "hsl(150 30% 12%)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{row.value}</Typography>
                </Tooltip>
                {row.copyValue && <CopyButton value={row.copyValue} sx={{ flexShrink: 0 }} />}
              </Box>
            </Box>
          ))}

          <Box>
            <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 0.3 }}>CV</Typography>
            {profile.cv_url ? (
              <Box
                component="a"
                href={profile.cv_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: 14,
                  color: "hsl(150 38% 17%)",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <FileText size={14} />
                {profile.cv_name || "View CV"}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 14, color: "hsl(150 30% 12%)" }}>—</Typography>
            )}
          </Box>

          <Box>
            <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 0.3 }}>Last status</Typography>
            {(() => {
              const badge = getOutcomeBadge(profile.last_action);
              return (
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    fontSize: 12,
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    px: "10px",
                    py: "4px",
                    borderRadius: "999px",
                    backgroundColor: badge.bg,
                    color: badge.text,
                  }}
                >
                  {badge.label}
                </Box>
              );
            })()}
          </Box>
        </Box>
      </Paper>

      {/* Notes card */}
      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid hsl(140 8% 88%)", p: "20px" }}>
        <Typography sx={{ fontSize: "10px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 1 }}>Notes</Typography>

        {/* Add note form */}
        <Box
          component="textarea"
          ref={textareaRef}
          value={noteContent}
          onChange={(e) => { setNoteContent(e.target.value); autoResize(); }}
          placeholder="Write a note about this profile..."
          rows={4}
          sx={{
            display: "flex",
            width: "100%",
            minHeight: "unset",
            borderRadius: "calc(0.75rem - 2px)",
            border: "1px solid transparent",
            backgroundColor: "hsl(140 10% 95%)",
            p: "8px",
            fontSize: "14px",
            color: "hsl(150 30% 12%)",
            resize: "none",
            outline: "none",
            overflow: "hidden",
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            boxSizing: "border-box",
            "&::placeholder": { color: "hsl(150 10% 46%)", opacity: 1 },
            "&:focus": { outline: "none", boxShadow: "none", borderColor: "hsl(140 8% 82%)" },
          }}
        />
        <Button
          disableRipple
          disableElevation
          disabled={savingNote || noteContent.trim().length === 0}
          onClick={addNote}
          sx={{
            mt: 1,
            width: "100%",
            fontSize: 13,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            px: 2,
            py: "8px",
            minHeight: 0,
            height: "auto",
            minWidth: 0,
            borderRadius: "0.6rem",
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            transition: "opacity 0.15s ease",
            "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
            "&.Mui-disabled": { opacity: 0.4, cursor: "not-allowed", pointerEvents: "auto", color: "hsl(80 97% 72%)", backgroundColor: "hsl(150 38% 17%)" },
          }}
        >
          {savingNote ? <CircularProgress size={14} sx={{ color: "hsl(80 97% 72%)" }} /> : <Plus size={14} />}
          {savingNote ? "Adding..." : "Add note"}
        </Button>

        {/* Notes list */}
        {loadingNotes ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} sx={{ color: "hsl(150 10% 46%)" }} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", mt: 2, textAlign: "center" }}>
            No notes yet
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {notes.map((n) => (
              <Box
                key={n.id}
                sx={{
                  p: 1.5,
                  borderRadius: "calc(0.75rem - 2px)",
                  backgroundColor: "var(--input-bg, hsl(140 10% 95%))",
                }}
              >
                <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 13, color: "hsl(150 30% 12%)", lineHeight: 1.55, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {n.content}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1, pt: 1, borderTop: "1px solid hsl(140 8% 88%)" }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <User size={11} color="hsl(150 10% 46%)" />
                    <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)" }}>{n.author || "Unknown"}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Clock size={11} color="hsl(150 10% 46%)" />
                    <Typography sx={{ fontSize: 11, color: "hsl(150 10% 46%)" }}>{formatNoteDate(n.created_at)}</Typography>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Outcome select + save */}
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ width: pendingOutcome === "follow_up_today" ? "66.666667%" : "100%" }}>
            <OutcomeSelect
              value={pendingOutcome}
              onChange={(val) => {
                setPendingOutcome(val);
                if (val !== "follow_up_today") setCallbackTime("");
                if (val === "sign_up_later") {
                  setCallbackDateTime((prev) => prev || defaultSignUpLaterValue());
                } else if (val === "follow_up_later") {
                  setCallbackDateTime((prev) => prev || defaultFollowUpLaterValue());
                } else {
                  setCallbackDateTime("");
                }
              }}
            />
          </Box>

          {pendingOutcome === "follow_up_today" && (
            <Box sx={{ width: "33.333333%", minWidth: 0 }}>
              <TimePicker value={callbackTime} onChange={setCallbackTime} />
            </Box>
          )}
        </Box>

        {(pendingOutcome === "sign_up_later" || pendingOutcome === "follow_up_later") && (
          <DateTimeSelect value={callbackDateTime} onChange={setCallbackDateTime} />
        )}

        {pendingOutcome && (
          <Box
            component="textarea"
            ref={(el) => {
              if (el) {
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
              }
            }}
            value={callNotes}
            onChange={(e) => {
              setCallNotes(e.target.value);
              const el = e.target;
              el.style.height = "auto";
              el.style.height = `${el.scrollHeight}px`;
            }}
            placeholder="Add a note about this call..."
            rows={3}
            sx={{
              display: "flex",
              width: "100%",
              minHeight: "unset",
              borderRadius: "calc(0.75rem - 2px)",
              border: "1px solid transparent",
              backgroundColor: "var(--input-bg, hsl(140 10% 95%))",
              p: "8px",
              fontSize: "14px",
              color: "hsl(150 30% 12%)",
              resize: "none",
              outline: "none",
              overflow: "hidden",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              boxSizing: "border-box",
              "&::placeholder": { color: "hsl(150 10% 46%)", opacity: 1 },
              "&:focus": { outline: "none", boxShadow: "none", borderColor: "hsl(140 8% 82%)" },
            }}
          />
        )}

        <Button
          disableRipple
          disableElevation
          disabled={!pendingOutcome}
          onClick={() => {
            if (pendingOutcome) {
              const followUpAt = (pendingOutcome === "sign_up_later" || pendingOutcome === "follow_up_later") && callbackDateTime
                ? new Date(callbackDateTime).toISOString()
                : undefined;
              onLogOutcome(pendingOutcome, callbackTime, callNotes, followUpAt);
              setPendingOutcome(null);
              setCallbackTime("");
              setCallbackDateTime("");
              setCallNotes("");
            }
          }}
          sx={{
            width: "100%",
            fontSize: "14px",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            px: "16px",
            py: "10px",
            lineHeight: "20px",
            minHeight: 0,
            minWidth: 0,
            height: "auto",
            borderRadius: "0.75rem",
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            transition: "opacity 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            textTransform: "none",
            "&:hover": {
              opacity: 0.9,
              backgroundColor: "hsl(150 38% 17%)",
            },
            "&.Mui-disabled": {
              opacity: 0.4,
              cursor: "not-allowed",
              pointerEvents: "auto",
              color: "hsl(80 97% 72%)",
              backgroundColor: "hsl(150 38% 17%)",
            },
          }}
        >
          <Check size={16} />
          Save outcome
        </Button>
      </Stack>

      {/* Call history */}
      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid hsl(140 8% 88%)", p: 2.5 }}>
        <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "hsl(150 10% 46%)", mb: 1.2 }}>Call history</Typography>
        {Array.isArray(profile.call_logs) && profile.call_logs.length > 0 ? (
          <Stack spacing={1.5}>
            {profile.call_logs.map((call) => (
              <CallHistoryItem key={call.id} call={call} />
            ))}
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>No calls yet.</Typography>
        )}
      </Paper>

      {(onSignUpToCandidate || profile.candidate_id) && (
        profile.candidate_id ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1.5,
              borderRadius: "12px",
              border: "1px solid hsl(140 8% 88%)",
              backgroundColor: "hsla(150, 38%, 17%, 0.05)",
            }}
          >
            <CheckCircle2 size={16} color="hsl(150 38% 17%)" />
            <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 38% 17%)" }}>
              Already on platform
            </Typography>
          </Box>
        ) : (
          <Button
            disableRipple
            disableElevation
            onClick={() => onSignUpToCandidate(profile)}
            sx={{
              width: "100%",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              px: 2,
              py: 1,
              minHeight: 0,
              height: "auto",
              minWidth: 0,
              borderRadius: "12px",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              transition: "opacity 0.15s ease",
              "&:hover": {
                backgroundColor: "hsl(150 38% 17%)",
                opacity: 0.9,
              },
            }}
          >
            <UserPlus size={16} />
            Sign up to platform
          </Button>
        )
      )}

      {!hideApplyButton && profile.candidate_id && (
        <Button
          disableRipple
          disableElevation
          onClick={() => setApplyOpen(true)}
          startIcon={<Send size={14} />}
          sx={{
            width: "100%",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            px: 2,
            py: 1,
            minHeight: 0,
            height: "auto",
            minWidth: 0,
            borderRadius: "12px",
            border: "1px solid hsl(140 8% 88%)",
            backgroundColor: "transparent",
            color: "hsl(150 30% 12%)",
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            transition: "background-color 0.15s ease",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          Apply to an offer
        </Button>
      )}

      {/* Apply to offer dialog */}
      <ApplyToOfferDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        candidateId={profile.candidate_id}
        candidateResumeUrl={profile.cv_url || profile.candidate_resume_url}
        appliedOfferIds={profile?.applied_offer_ids || []}
        initialOfferIds={initialOfferIds}
        onApplied={onApplied}
      />
    </Stack>
  );
};

export default ProfileDetailPanel;
